import qs from 'qs';
import isEmpty from 'lodash/isEmpty';

export const VIEW_URL_BASE = '/sites/view/';
export const SITE_HOSTNAME = (() => window.location.hostname)();
export const QUERY_PARAM_FORM_PREVIEW_ID = 'formPreviewId';

/**
 * Change the app subdomain to the original domain.
 * Helpful for different development environments
 * @return {string} [description]
 * ex: if origin is 'https://app.splashthat.com', returns 'https://splashthat.com'
 * ex: if origin is 'https://splashlocal.com/nav', returns 'https://splashlocal.com'
 */
export const getOriginWithoutAppSubdomain = () =>
    window.location.origin.replace('.com/nav', '.com/').replace('app.', '');

/**
 * @param  {string} relativeUrl
 * @return {string}
 * ex: relativeUrl '/sites/view/bloboctopus.splashthat.com'
 *     returns 'https://splashthat.com/sites/view/bloboctopus.splashthat.com'
 */
export const getAbsoluteNonAppUrl = (relativeUrl) => {
    const origin = getOriginWithoutAppSubdomain();
    let parsedUrl = relativeUrl;

    if (relativeUrl[0] !== '/') {
        parsedUrl = `/${relativeUrl}`;
    }

    return `${origin}${parsedUrl}`;
};

/**
 * Works as a switch between Splash 4 event page urls and existing event page urls
 *
 * @param  {string} domain
 * @return {string}
 * ex: eventUrl '/sites/view/drinkmorewater.splashthat.com', eventId 516215
 *     returns 'events/drinkmorewater'
 */
export function getEditEventUrl(domain) {
    return `/events/${domain}/`;
}

/**
 * @param  {string} eventUrl
 * @return {string}
 * ex: eventUrl 'https://puzzlebobbleparty.splashthat.com'
 *     returns '/sites/view/puzzlebobbleparty.splashthat.com'
 */
export const makeFullyQualifiedEventUrlRelative = (eventUrl) =>
    /^https?:\/\/(\w+\.)?splashthat\.com/.test(eventUrl)
        ? eventUrl.replace('https://', VIEW_URL_BASE)
        : `${VIEW_URL_BASE}${eventUrl}`;

/**
 * @param  {string} eventDomain ONLY the event domain, not the fully qualified version
 *                              since that could be the white-labeled address
 * @param  {object} queryParams Query param name key to param value
 * @return {string}
 *
 * ex: eventDomain 'bubblebobblebonanza'
 *     returns 'www.splashthat.com/sites/view/bubblebobblebonanza.splashthat.com'
 * ex: eventDomain 'puzzlebobbleparty', queryParams { isCute: 1 }
 *     returns 'www.splashthat.com/sites/view/puzzlebobbleparty.splashthat.com?isCute=1'
 */
export const getEventPageUrlFromEventDomain = (eventDomain, queryParams = {}) => {
    const siteDomain = getOriginWithoutAppSubdomain();
    let queryString = qs.stringify(queryParams);

    if (queryString.length) {
        queryString = `?${queryString}`;
    }

    // eslint-disable-next-line max-len
    return `${siteDomain}${VIEW_URL_BASE}${eventDomain}.${SITE_HOSTNAME.replace('app.', '')}${queryString}`;
};

/**
 * @param  {string} eventUrl
 * @return {string}
 * ex: eventUrl 'https://puzzlebobbleparty.splashthat.com'
 *     returns 'https://splashthat.com/sites/view/puzzlebobbleparty.splashthat.com?preview'
 */
export const getPreviewEventUrl = (eventUrl) =>
    `${getOriginWithoutAppSubdomain()}${makeFullyQualifiedEventUrlRelative(eventUrl)}?preview`;

/**
 * @param  {string} eventDomain ONLY the event domain, not the fully qualified version
 *                              since that could be the white-labeled address
 * @param  {string|number} stageId  Optional stage id to preview on the event page
 * @return {string}
 *
 * ex: eventDomain 'bubblebobblebonanza'
 *     returns 'www.splashthat.com/sites/view/bubblebobblebonanza.splashthat.com?preview=1'
 * ex: eventDomain 'puzzlebobbleparty', stageId '42'
 *     returns 'www.splashthat.com/sites/view/puzzlebobbleparty.splashthat.com?preview=1&stage=43'
 * ex: eventDomain 'itsababybottlepop', shouldOpenForm true
 *     returns 'www.splashthat.com/sites/view/itsababybottlepop.splashthat.com?preview=1&autoopen=1'
 */
export const getPreviewEventUrlFromEventDomain = (
    eventDomain,
    stageId = null,
    shouldOpenForm = false,
    formPreviewId = null,
) => {
    const previewParams = { preview: true };

    if (stageId) {
        previewParams.stage = stageId;
    }

    if (shouldOpenForm) {
        previewParams.autoopen = 1;
    }

    if (formPreviewId) {
        previewParams[QUERY_PARAM_FORM_PREVIEW_ID] = formPreviewId;
    }

    return `${getEventPageUrlFromEventDomain(eventDomain, previewParams)}`;
};

/**
 * @param  {string} path    (React-router)
 * @param  {Object} object of the param names to their values
 * @return {string} Resolved path. If param isn't set, it strips it from the url
 *
 * ex: path '/email/:emailId', paramsMap { emailId: 829 }
 *      returns '/email/829'
 * ex: path '/event-page/:stageId', paramsMap {}
 *      returns '/event-page'
 */
export const setParametersInPath = (path, paramsMap = {}) => {
    const pathParts = path.split('/');

    const parsedPathParts = pathParts
        .map((part) => {
            // If not a dynamic parameter, do nothing
            if (part[0] !== ':') {
                return part;
            }

            let paramName = part.match(/:(\w*)/);
            paramName = paramName && paramName[1];

            if (paramName && paramsMap[paramName]) {
                return encodeURIComponent(paramsMap[paramName]);
            } else if (path[path.length - 1] !== '?') {
                // If the param is not optional but wasn't passed, throw an error
                throw new Error('Required parameter not passed');
            }

            return '';
        })
        .filter(Boolean);

    return `${path[0] === '/' ? '/' : ''}${parsedPathParts.join('/')}`;
};

/**
 * Return the vanity url if there is one, else the constructed public domain
 *
 * Ex formatDomain('splashthat.com', 'my-cool-party') => my-cool-party.splashthat.com
 * Ex formatDomain('splashthat.com', 'my-cool-party', 'partytime.com') => partytime.com
 *
 * @param {string} rootDomain - splashthat.com, splashlocal.com, etc.
 * @param {string} domain - the domain field on an Event (no `.splashthat.com`) suffix
 * @param {string} customDomain - the full vanity url
 * @param {object} queryparams - query params eg stageID
 * @return {string}
 */
export const formatDomain = (rootDomain, domain, customDomain = null, queryParams = {}) => {
    let queryString = '';

    if (!rootDomain || !domain) {
        return '';
    }

    if (!isEmpty(queryParams)) {
        queryString = `?${qs.stringify(queryParams)}`;
    }

    if (customDomain) {
        return `${customDomain}${queryString}`;
    }

    return `${domain}.${rootDomain}${queryString}`;
};
